import { useEffect } from "react";

const SITE_URL = "https://www.mauriziobrioschi.com";
const DEFAULT_IMAGE = `${SITE_URL}/cover1.png`;
const DEFAULT_ROBOTS = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

const PERSON_ID = `${SITE_URL}/#person`;
const SERVICE_ID = `${SITE_URL}/#professional-service`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const sameAsProfiles = [
    "https://www.linkedin.com/in/mauriziobrioschi/",
    "https://github.com/mbrioski"
];

const knowledgeAreas = [
    "Fractional CTO",
    "Solution Architecture",
    "Cloud Architecture",
    "Digital Transformation",
    "AWS",
    "Laravel",
    "Magento",
    "GraphQL",
    "React",
    "Docker",
    "Microservices",
    "CI/CD",
    "Salesforce integrations",
    "Ticketing integrations",
    "High traffic portals",
    "SaaS platforms",
    "Agile engineering leadership"
];

const ensurePath = (path = "/") => (path.startsWith("/") ? path : `/${path}`);
const toAbsoluteUrl = (path = "/") => `${SITE_URL}${ensurePath(path)}`;

const upsertMeta = ({ attr, key, content }) => {
    const selector = `meta[${attr}="${key}"]`;
    let tag = document.head.querySelector(selector);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
};

const upsertLink = ({ key, rel, href, hreflang }) => {
    const selector = `link[data-seo-key="${key}"]`;
    const fallbackSelector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
    let tag = document.head.querySelector(selector) || document.head.querySelector(fallbackSelector);
    if (!tag) {
        tag = document.createElement("link");
        document.head.appendChild(tag);
    }
    tag.setAttribute("data-seo-key", key);
    tag.setAttribute("rel", rel);
    tag.setAttribute("href", href);
    if (hreflang) {
        tag.setAttribute("hreflang", hreflang);
    } else {
        tag.removeAttribute("hreflang");
    }
};

const upsertStructuredData = (key, value) => {
    const selector = `script[data-structured-data="${key}"]`;
    let tag = document.head.querySelector(selector);
    if (!tag && key === "global-graph") {
        tag = document.head.querySelector("script[data-structured-data='bootstrap-global-graph']");
    }
    if (!tag) {
        tag = document.createElement("script");
        tag.type = "application/ld+json";
        document.head.appendChild(tag);
    }
    tag.setAttribute("data-structured-data", key);
    tag.textContent = JSON.stringify(value);
};

const removeStructuredData = (key) => {
    const tag = document.head.querySelector(`script[data-structured-data="${key}"]`);
    if (tag) {
        tag.remove();
    }
};

const buildBreadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: toAbsoluteUrl(item.path)
    }))
});

const buildFaqSchema = (faqItems) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
            "@type": "Answer",
            text: item.answer
        }
    }))
});

const buildGlobalGraph = () => ({
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Person",
            "@id": PERSON_ID,
            name: "Maurizio Brioschi",
            url: SITE_URL,
            jobTitle: "Fractional CTO | Solution Architect | Digital Transformation Consultant",
            sameAs: sameAsProfiles,
            knowsAbout: knowledgeAreas,
            worksFor: {
                "@id": SERVICE_ID
            },
            description:
                "Maurizio Brioschi is a Fractional CTO, Solution Architect, Cloud Architect, and Digital Transformation Consultant with 25+ years of experience in software engineering, architecture modernization, AWS cloud strategy, platform scalability, and technical leadership."
        },
        {
            "@type": "ProfessionalService",
            "@id": SERVICE_ID,
            name: "Maurizio Brioschi Consulting",
            url: SITE_URL,
            image: DEFAULT_IMAGE,
            description:
                "Fractional CTO, solution architecture, cloud architecture, and digital transformation consulting for startups, scaleups, and established companies.",
            founder: {
                "@id": PERSON_ID
            },
            provider: {
                "@id": PERSON_ID
            },
            serviceType: [
                "Fractional CTO",
                "Solution Architecture",
                "Cloud Architecture",
                "Digital Transformation Consulting",
                "Technical Leadership"
            ],
            areaServed: [
                "Italy",
                "Europe",
                "United Kingdom"
            ]
        },
        {
            "@type": "WebSite",
            "@id": WEBSITE_ID,
            name: "Maurizio Brioschi",
            url: SITE_URL,
            publisher: {
                "@id": PERSON_ID
            },
            inLanguage: [
                "en",
                "it"
            ]
        }
    ]
});

const buildWebPageSchema = ({ title, description, locale, path, breadcrumbItems }) => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${toAbsoluteUrl(path)}#webpage`,
    url: toAbsoluteUrl(path),
    name: title,
    description,
    inLanguage: locale,
    isPartOf: {
        "@id": WEBSITE_ID
    },
    about: [
        {
            "@id": PERSON_ID
        },
        {
            "@id": SERVICE_ID
        }
    ],
    breadcrumb: breadcrumbItems?.length
        ? {
            "@id": `${toAbsoluteUrl(path)}#breadcrumb`
        }
        : undefined,
    primaryImageOfPage: {
        "@type": "ImageObject",
        url: DEFAULT_IMAGE
    }
});

function SeoHead({
    title,
    description,
    keywords,
    path = "/",
    locale = "en",
    enPath = "/",
    itPath = "/it",
    image = DEFAULT_IMAGE,
    ogType = "website",
    breadcrumbItems = [],
    faqItems = []
}) {
    useEffect(() => {
        const canonicalUrl = toAbsoluteUrl(path);
        const localeCode = locale === "it" ? "it_IT" : "en_GB";

        document.title = title;
        document.documentElement.lang = locale;

        upsertMeta({ attr: "name", key: "description", content: description });
        upsertMeta({ attr: "name", key: "keywords", content: keywords });
        upsertMeta({ attr: "name", key: "author", content: "Maurizio Brioschi" });
        upsertMeta({ attr: "name", key: "creator", content: "Maurizio Brioschi" });
        upsertMeta({ attr: "name", key: "publisher", content: "Maurizio Brioschi" });
        upsertMeta({ attr: "name", key: "robots", content: DEFAULT_ROBOTS });
        upsertMeta({ attr: "name", key: "googlebot", content: DEFAULT_ROBOTS });
        upsertMeta({ attr: "name", key: "bingbot", content: DEFAULT_ROBOTS });
        upsertMeta({ attr: "name", key: "referrer", content: "strict-origin-when-cross-origin" });
        upsertMeta({ attr: "name", key: "format-detection", content: "telephone=no" });
        upsertMeta({ attr: "property", key: "og:title", content: title });
        upsertMeta({ attr: "property", key: "og:description", content: description });
        upsertMeta({ attr: "property", key: "og:type", content: ogType });
        upsertMeta({ attr: "property", key: "og:url", content: canonicalUrl });
        upsertMeta({ attr: "property", key: "og:image", content: image });
        upsertMeta({ attr: "property", key: "og:image:alt", content: "Maurizio Brioschi consulting website cover" });
        upsertMeta({ attr: "property", key: "og:site_name", content: "Maurizio Brioschi" });
        upsertMeta({ attr: "property", key: "og:locale", content: localeCode });
        upsertMeta({ attr: "name", key: "twitter:card", content: "summary_large_image" });
        upsertMeta({ attr: "name", key: "twitter:title", content: title });
        upsertMeta({ attr: "name", key: "twitter:description", content: description });
        upsertMeta({ attr: "name", key: "twitter:image", content: image });
        upsertMeta({ attr: "name", key: "twitter:url", content: canonicalUrl });

        upsertLink({ key: "canonical", rel: "canonical", href: canonicalUrl });
        upsertLink({ key: "alternate-en", rel: "alternate", href: toAbsoluteUrl(enPath), hreflang: "en" });
        upsertLink({ key: "alternate-it", rel: "alternate", href: toAbsoluteUrl(itPath), hreflang: "it" });
        upsertLink({ key: "alternate-default", rel: "alternate", href: toAbsoluteUrl(enPath), hreflang: "x-default" });

        upsertStructuredData("global-graph", buildGlobalGraph());
        upsertStructuredData("webpage", buildWebPageSchema({ title, description, locale, path, breadcrumbItems }));

        if (breadcrumbItems.length) {
            upsertStructuredData("breadcrumb", {
                ...buildBreadcrumbSchema(breadcrumbItems),
                "@id": `${canonicalUrl}#breadcrumb`
            });
        } else {
            removeStructuredData("breadcrumb");
        }

        if (faqItems.length) {
            upsertStructuredData("faq", buildFaqSchema(faqItems));
        } else {
            removeStructuredData("faq");
        }
    }, [title, description, keywords, path, locale, enPath, itPath, image, ogType, breadcrumbItems, faqItems]);

    return null;
}

export default SeoHead;
